import { CdkAccordion } from '@angular/cdk/accordion';
import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { PageEvent } from '@angular/material/paginator';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { TranslocoPipe } from '@jsverse/transloco';
import { lastValueFrom } from 'rxjs';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { Assignment } from '../../../api/app-types/assignment';
import { Contact } from '../../../api/app-types/contact.app';
import { DB_PHASES } from '../../../api/app-types/phase.app';
import { User } from '../../../api/app-types/user.app';
import { ADDITIONAL_ASSIGNMENT_API } from '../../../api/services/additional-assignment.service';
import { SPONSOR_SERVICE } from '../../../api/services/sponsor.service';
import { UPLOAD_API } from '../../../api/services/upload.api';
import { ContactCard } from '../../../features/contact/contact-card/contact-card';
import { registerSvgIcon } from '../../../shared/helpers/icon-register.func';
import { hasPermission } from '../../../shared/permissions/permission.func';
import { RouteInjection } from '../../../shared/routing/route-injection.class';
import { LocalStorageService } from '../../../shared/storage/local-storage.service';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';
import { AccessService } from '../../services/access.service';
import { LOADING } from '../../services/loading.service';
import { REPRESENTATION_MODE } from '../../services/representation-mode.service';
import { STATE } from '../../services/state.service';
import { LvrAssignmentAccordionItem } from '../assignment-accordion-item/assignment-accordion-item';
import { GenericDeleteDialog } from '../generic-delete-dialog/generic-delete-dialog';
import { LvrButton } from '../lvr-button/lvr-button';
import { Paginator } from '../paginator/paginator';
import { LvrSection } from '../section/section';

@Component({
	selector: 'lvr-assignment-accordion',
	templateUrl: './assignment-accordion.html',
	styleUrl: './assignment-accordion.scss',
	imports: [
		LvrAssignmentAccordionItem,
		CdkAccordion,
		ContactCard,
		RouterLink,
		TranslocoPipe,
		DatePipe,
		RouterLink,
		LvrSection,
		MatDivider,
		MatTooltip,
		MatDialogModule,
		Paginator,
		LvrButton,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LvrAssignmentAccordion extends RouteInjection {
	public get DB_PHASES() {
		return DB_PHASES;
	}
	private readonly environment = inject(ENVIRONMENT);
	private readonly auth = inject(MsalService, { optional: true });
	private readonly storage = inject(LocalStorageService, { optional: true });
	protected readonly loading = inject(LOADING);
	private readonly uploadApi = inject(UPLOAD_API);
	private readonly session = inject(SessionManagerService);
	private representationMode = inject(REPRESENTATION_MODE);
	private readonly state = inject(STATE);
	private readonly access = inject(AccessService);
	private readonly sponsorApi = inject(SPONSOR_SERVICE, { optional: true });
	private readonly additionalAssignmentApi = inject(ADDITIONAL_ASSIGNMENT_API, { optional: true });
	private readonly dialog = inject(MatDialog);
	private readonly sponsorNamesById = signal<Record<number, string>>({});
	private readonly loadingSponsorIds = new Set<number>();
	public readonly assignments = input.required<Assignment[]>();
	public readonly contactLoadingByAssignmentId = input<Record<number, boolean>>({});
	public readonly contactErrorByAssignmentId = input<Record<number, boolean>>({});
	public readonly currentUserId = input<string | null>(null);
	public readonly currentUser = input<User>();
	public readonly isAdmin = computed(() => this.currentUser()?.roles?.shortName?.toLowerCase() === 'admin');
	public readonly toggleOnIconOnly = input<boolean>(false);
	public readonly loadingAssignments = input(false);

	public readonly clientId = computed(() => this.assignments().at(0)?.clientId);
	private readonly isFdsAssignment = (assignment: Assignment) => {
		return assignment.isServiceOfficeStatement === true && assignment.phaseId === DB_PHASES.FP3;
	};
	public readonly fdsAssignments = computed(() => this.assignments().filter((a) => this.isFdsAssignment(a)));
	public readonly activeAssignments = computed(() =>
		this.assignments().filter(
			(a) => a.phaseId !== DB_PHASES.P4 && !a.isServiceOfficeStatement && !a.isAdditionalAssignment,
		),
	);
	public readonly additionalAssignmentsInRestrictedView = computed(() =>
		this.activeAssignments().flatMap(
			(assignment) =>
				assignment.additionalAssignments?.filter(
					(a) => a.phaseId !== DB_PHASES.P4 && !a.isServiceOfficeStatement && a.isAdditionalAssignment,
				) ?? [],
		),
	);
	public readonly finishedAssignments = computed(() =>
		this.assignments().filter(
			(a) => a.phaseId === DB_PHASES.P4 && !a.isServiceOfficeStatement && !a.isAdditionalAssignment,
		),
	);
	public readonly finishedAdditionalAssignments = computed(() =>
		this.activeAssignments().flatMap(
			(assignment) =>
				assignment.additionalAssignments?.filter(
					(a) => a.phaseId === DB_PHASES.P4 && !a.isServiceOfficeStatement && a.isAdditionalAssignment,
				) ?? [],
		),
	);
	public readonly finishedFdsAssignments = computed(() =>
		this.assignments().filter(
			(a) =>
				(a.phaseId === DB_PHASES.FP4 || a.phaseId === DB_PHASES.FP5) &&
				a.isServiceOfficeStatement &&
				!a.isAdditionalAssignment,
		),
	);

	private readonly assignmentInTransitionForUser = computed(() => {
		const user = this.currentUser();
		if (!user) return undefined;

		const transitions = this.state
			.currentAssignments()
			.flatMap((assignment) => [assignment, ...(assignment.additionalAssignments ?? [])])
			.filter(
				(assignment) =>
					assignment.phaseChangeId != null &&
					assignment.phaseChangeId > 0 &&
					assignment.phaseChangeId !== assignment.phaseId,
			);

		return transitions.find((assignment) =>
			assignment.isAdditionalAssignment
				? this.access.isAdditionalOwner(assignment, user)
				: this.access.isMainOwner(assignment, user),
		);
	});

	public readonly hasActivePhaseTransitionForUser = computed(() => !!this.assignmentInTransitionForUser());

	public readonly openMainId = this.resolveParamAsSignal('openMain');
	public readonly openChildId = this.resolveParamAsSignal('openChild');

	public readonly showFirstLastButtons = true;
	private readonly pageStateById = signal<Record<string, { pageIndex: number; pageSize: number }>>({});

	public shouldOpenMain(item: Assignment) {
		const openMain = Number(this.openMainId());
		const openChild = Number(this.openChildId());

		if (!openMain && !openChild) {
			const closedPhases = [DB_PHASES.P4, DB_PHASES.FP4, DB_PHASES.FP5];
			if (closedPhases.includes(item.phaseId)) return false;
			const firstOpenCandidate = this.assignments().find(
				(assignment) => !assignment.isAdditionalAssignment && !closedPhases.includes(assignment.phaseId),
			);
			return firstOpenCandidate?.id === item.id;
		}

		if (openMain && item.id === openMain) {
			if (openChild) {
				const childIsFinished = (item.additionalAssignments ?? []).some(
					(a) => a.id === openChild && a.phaseId === DB_PHASES.P4,
				);

				if (childIsFinished) return false;
			}
			return true;
		}
		return false;
	}

	public shouldOpenAdditional(additional: Assignment) {
		const openChild = Number(this.openChildId());
		return openChild != null && additional.id === openChild;
	}

	public shouldExpandAdditional(additional: Assignment): boolean {
		const openChild = Number(this.openChildId());

		if (openChild) {
			return additional.id === openChild;
		}

		return this.isAdditionalOwner(additional);
	}

	public isPhaseP3(assignment: Assignment) {
		return assignment.phaseId === DB_PHASES.P3;
	}

	public additionalAssignmentsFor(item: Assignment) {
		return item.additionalAssignments?.filter((assignment) => assignment.phaseId !== DB_PHASES.P4) ?? [];
	}

	public getAdditionalAssignmentTitleKey(additional: Assignment): string {
		switch (additional.phaseId) {
			case DB_PHASES.P1:
				return 'assignment.additional.draft_title';
			case DB_PHASES.P3:
				return 'assignment.additional.phase_p3_title';
			case DB_PHASES.P4:
				return 'assignment.additional.phase_p4_title';
			default:
				return 'assignment.additional.title';
		}
	}

	public hasOpenAdditionalAssignments(item: Assignment): boolean {
		const additional = item.additionalAssignments ?? [];
		if (additional.length === 0) return false;
		return additional.some((assignment) => assignment.phaseId !== DB_PHASES.P4);
	}

	public isPhaseTransitionDisabled(): boolean {
		return this.isRepresentativeMode() || this.hasActivePhaseTransitionForUser() || !this.canTransitionPhases();
	}

	public isPhaseTransitionDisabledForAssignment(item: Assignment): boolean {
		return this.isPhaseTransitionDisabled() || !this.isAssignmentOwner(item);
	}

	public isPhaseEndDisabledForMainAssignment(item: Assignment): boolean {
		return (
			this.isRepresentativeMode() ||
			this.hasActivePhaseTransitionForUser() ||
			!this.isMainOwner(item) ||
			(this.isPhaseP3(item) && this.hasOpenAdditionalAssignments(item)) ||
			!this.canTransitionPhases()
		);
	}

	public getPhaseTransitionDisabledTooltipKey(): string | null {
		if (this.isRepresentativeMode()) {
			return 'assignment.phase.representative_no_phase_change';
		}

		if (this.hasActivePhaseTransitionForUser()) {
			return 'assignment.phase.active_phase_change_in_progress';
		}

		return null;
	}

	public getPhaseSubmitDisabledTooltipKey(): string | null {
		if (this.isRepresentativeMode()) {
			return 'assignment.phase.representative_no_submit';
		}

		if (this.hasActivePhaseTransitionForUser()) {
			return 'assignment.phase.active_phase_change_in_progress';
		}

		return null;
	}

	public getPhaseCancelDisabledTooltipKey(): string | null {
		if (this.isRepresentativeMode()) {
			return 'assignment.phase.representative_no_cancel';
		}

		if (this.hasActivePhaseTransitionForUser()) {
			return 'assignment.phase.active_phase_change_in_progress';
		}

		return null;
	}

	public getPhaseReturnDisabledTooltipKey(): string | null {
		if (this.isRepresentativeMode()) {
			return 'assignment.phase.representative_no_return';
		}

		if (this.hasActivePhaseTransitionForUser()) {
			return 'assignment.phase.active_phase_change_in_progress';
		}

		return null;
	}

	public isMainOwner(item: Assignment): boolean {
		const currentUserId = this.currentUserId();
		if (currentUserId == null) return false;
		const currentIds = new Set([String(currentUserId)]);
		if (item.createdByUser) {
			const createdByIds = [item.createdByUser.id, item.createdByUser.objectId].filter((id) => id != null).map(String);
			return createdByIds.some((id) => currentIds.has(id));
		}
		const additionalOwners = (item.additionalAssignments ?? [])
			.flatMap((assignment) => [assignment.createdByUser?.id, assignment.ownerOfAdditionalAssignment?.id])
			.filter((id) => !!id)
			.map(String);
		if (additionalOwners.length === 0) return true;
		return !additionalOwners.some((id) => currentIds.has(id));
	}

	public isAdditionalOwner(item: Assignment): boolean {
		const currentUserId = this.currentUserId();
		if (currentUserId == null) return false;
		const currentIds = new Set([String(currentUserId)]);
		const ownerIds = item.ownerOfAdditionalAssignment
			? [item.ownerOfAdditionalAssignment.id, item.ownerOfAdditionalAssignment.objectId]
					.filter((id) => id != null)
					.map(String)
			: item.createdByUser
				? [item.createdByUser.id, item.createdByUser.objectId].filter((id) => id != null).map(String)
				: [];
		return ownerIds.some((id) => currentIds.has(id));
	}

	public isAssignmentOwner(item: Assignment): boolean {
		const currentUserId = this.currentUserId();
		if (currentUserId == null) return false;
		const currentIds = new Set([String(currentUserId)]);
		const ownerIds = [item.createdByUser?.id, item.ownerOfAdditionalAssignment?.id].filter((id) => !!id).map(String);
		return ownerIds.some((id) => currentIds.has(id));
	}

	private isFdsAssignmentOwner(item: Assignment): boolean {
		const currentUserId = this.currentUserId();
		if (currentUserId == null) return false;
		if (!item.isServiceOfficeStatement) return false;

		const currentIds = new Set([String(currentUserId)]);

		const ownerIds = [item.createdByUser?.id, item.createdByUser?.objectId].filter((id) => id != null).map(String);

		return ownerIds.some((id) => currentIds.has(id));
	}

	public readonly createLink = input<string>('');
	public readonly createLinkForAssignment = input<((assignment: Assignment) => string) | null>(null);
	public readonly move = output<Assignment>();
	public readonly moveToCancel = output<Assignment>();
	public readonly additionalDeleted = output<Assignment>();
	public readonly isRepresentativeMode = this.representationMode.isActive;
	public readonly canDeleteAssignments = hasPermission(this.session.permissions, 'Assignments.Delete');
	public readonly canReadContactsRestricted = hasPermission(this.session.permissions, 'Contacts.Read.Restricted');
	public readonly canReadContacts = hasPermission(this.session.permissions, 'Contacts.Read');
	public readonly canTransitionPhases = hasPermission(this.session.permissions, 'Phases.Transition');

	public readonly canCreateContactPhaseOneOnly = hasPermission(
		this.session.permissions,
		'Contacts.Create.PhaseOneOnly',
	);

	public readonly canCreateContact = hasPermission(this.session.permissions, 'Contacts.Create');

	private isRepresentingUser(userId?: number | string | null): boolean {
		const representedUserId = this.representationMode.mode().representedUserId;

		if (representedUserId == null || userId == null) return false;

		return String(representedUserId) === String(userId);
	}

	private isRepresentingAdditionalAssignmentOwner(item: Assignment): boolean {
		const owner = item.ownerOfAdditionalAssignment ?? item.createdByUser;

		return this.isRepresentingUser(owner?.id);
	}

	public canAddContactToItem(item: Assignment): boolean {
		const hasCreatePermission = this.canCreateContactPhaseOneOnly() || this.canCreateContact();
		const isRepresentingAssignmentOwner = this.isRepresentingUser(item.createdByUser?.id);
		const isRepresentingAdditionalAssignmentOwner = this.isRepresentingAdditionalAssignmentOwner(item);

		if (item.isAdditionalAssignment) {
			return (
				(this.isAdditionalOwner(item) && !this.isRepresentativeMode()) ||
				(this.isRepresentativeMode() && hasCreatePermission && isRepresentingAdditionalAssignmentOwner)
			);
		}

		if (item.isServiceOfficeStatement) {
			return (
				(this.isFdsAssignmentOwner(item) && !this.isRepresentativeMode()) ||
				(this.isRepresentativeMode() && hasCreatePermission && isRepresentingAssignmentOwner)
			);
		}

		return (
			(this.isMainOwner(item) && !this.isRepresentativeMode()) ||
			(this.isRepresentativeMode() && hasCreatePermission && isRepresentingAssignmentOwner)
		);
	}

	public canExportContacts(item: Assignment): boolean {
		const hasReadContactsPermission = this.canReadContacts() || this.canReadContactsRestricted();
		const isRepresentingAssignmentOwner = this.isRepresentingUser(item.createdByUser?.id);
		const isRepresentingAdditionalAssignmentOwner = this.isRepresentingAdditionalAssignmentOwner(item);

		if (item.isAdditionalAssignment) {
			return (
				(this.isAdditionalOwner(item) && !this.isRepresentativeMode()) ||
				(this.isRepresentativeMode() && hasReadContactsPermission && isRepresentingAdditionalAssignmentOwner)
			);
		}

		if (item.isServiceOfficeStatement) {
			return (
				(this.isFdsAssignmentOwner(item) && !this.isRepresentativeMode()) ||
				(this.isRepresentativeMode() && hasReadContactsPermission && isRepresentingAssignmentOwner)
			);
		}

		return (
			(this.isMainOwner(item) && !this.isRepresentativeMode()) ||
			(this.isRepresentativeMode() && hasReadContactsPermission && isRepresentingAssignmentOwner)
		);
	}
	private readonly preloadSponsorNames = effect(() => {
		const assignmentList = this.assignments();
		const ids = new Set<number>();

		for (const assignment of assignmentList) {
			if (assignment.sponsorId) ids.add(assignment.sponsorId);
			for (const additional of assignment.additionalAssignments ?? []) {
				if (additional.sponsorId) ids.add(additional.sponsorId);
			}
		}

		const cache = this.sponsorNamesById();
		for (const id of ids) {
			if (!cache[id]) {
				void this.loadSponsorName(id);
			}
		}
	});

	public constructor() {
		super();
		registerSvgIcon('tab_close_right', '/assets/icons/tab_close_right.svg');
	}

	public switchPhase(event: Event, assignment: Assignment) {
		event.preventDefault();
		event.stopPropagation();

		if (this.hasActivePhaseTransitionForUser()) return;

		this.moveToCancel.emit(assignment);
	}

	public triggerPhaseMove(event: Event, assignment: Assignment) {
		event.preventDefault();
		event.stopPropagation();

		if (this.hasActivePhaseTransitionForUser()) return;

		this.move.emit(assignment);
	}

	public getCreateLink(item: Assignment): string {
		const linkFactory = this.createLinkForAssignment();
		if (linkFactory) return linkFactory(item);
		return this.createLink();
	}

	public openContactCreate(item: Assignment) {
		this.router.navigate([this.getCreateLink(item)], { relativeTo: this.route });
	}

	public canCreateContactForFds(item: Assignment): boolean {
		return item.isServiceOfficeStatement === true && item.phaseId === DB_PHASES.FP3;
	}

	public async deleteAdditional(event: Event, additional: Assignment) {
		event.preventDefault();
		event.stopPropagation();
		if (!this.canDeleteAssignments()) return;
		if (!this.additionalAssignmentApi) return;
		const confirmed = await lastValueFrom(
			this.dialog
				.open(GenericDeleteDialog, {
					data: {
						titleKey: 'assignment.additional.delete_dialog.title',
						messageKey: 'assignment.additional.delete_dialog.message',
						confirmKey: 'assignment.additional.delete_dialog.confirm',
						cancelKey: 'assignment.additional.delete_dialog.cancel',
					},
				})
				.afterClosed(),
		);
		if (!confirmed) return;
		await this.additionalAssignmentApi.delete(additional.clientId, additional.id);
		this.additionalDeleted.emit(additional);
	}

	public getPhaseStartDate(item: Assignment) {
		if (item.isServiceOfficeStatement) {
			switch (item.phaseId) {
				case DB_PHASES.FP3:
					return item.careStartDate ?? item.phaseStart ?? null;
				case DB_PHASES.FP4:
				case DB_PHASES.FP5:
					return item.cancellationDate;
				default:
					return null;
			}
		}

		switch (item.phaseId) {
			case DB_PHASES.P0:
			case DB_PHASES.P1:
				return item.contactStart;
			case DB_PHASES.P2:
				return item.consultationDate;
			case DB_PHASES.P3:
				return item.careStartDate;
			case DB_PHASES.P4:
				return item.cancellationDate;
			default:
				return null;
		}
	}

	public getSponsorName(item: Assignment): string | null {
		const ext = item as Assignment & { sponsor?: { name?: string } };
		const relatedSponsorName = ext.sponsor?.name?.trim();
		if (relatedSponsorName) return relatedSponsorName;

		if (!item.sponsorId) return null;
		return this.sponsorNamesById()[item.sponsorId] ?? null;
	}

	private async loadSponsorName(sponsorId: number): Promise<void> {
		if (!this.sponsorApi) return;
		if (this.loadingSponsorIds.has(sponsorId)) return;
		if (this.sponsorNamesById()[sponsorId]) return;

		this.loadingSponsorIds.add(sponsorId);
		try {
			const sponsor = await this.sponsorApi.getById(sponsorId);
			if (!sponsor?.name) return;
			this.sponsorNamesById.update((state) => ({ ...state, [sponsorId]: sponsor.name }));
		} finally {
			this.loadingSponsorIds.delete(sponsorId);
		}
	}

	public isDraft(item: Assignment) {
		return item.phaseId === DB_PHASES.P1 && item.draftPhaseId != null;
	}

	private getCareType(item: Assignment): string {
		const ext = item as Assignment & {
			typeOfCare?: { remark?: string } | string;
			categoryTypeOfCare?: { remark?: string };
			typeOfCareRemark?: string;
		};

		if (typeof ext.typeOfCare === 'string' && ext.typeOfCare.trim().length > 0) {
			return ext.typeOfCare;
		}

		const relationRemark = typeof ext.typeOfCare === 'object' ? ext.typeOfCare?.remark : undefined;
		return relationRemark?.trim() || ext.categoryTypeOfCare?.remark?.trim() || ext.typeOfCareRemark?.trim() || '-';
	}

	public getMainAccordionTitleKey(item: Assignment): { key: string; params?: Record<string, unknown> } {
		if (item.phaseId === DB_PHASES.P1) {
			return { key: 'base.phase.p1' };
		}

		return this.getAccordionTitleKey(item);
	}

	public getAccordionTitleKey(item: Assignment): { key: string; params?: Record<string, unknown> } {
		const careType = this.getCareType(item);

		if (item.isServiceOfficeStatement || item.phaseId >= DB_PHASES.FP3) {
			switch (item.phaseId) {
				case DB_PHASES.FP3:
					return { key: 'assignment.title.fds_fp3' };
				case DB_PHASES.FP4:
					return { key: 'assignment.title.fds_fp4' };
				case DB_PHASES.FP5:
					return { key: 'assignment.title.fds_fp5' };
				default:
					return { key: 'assignment.fds.title' };
			}
		}

		if (item.isAdditionalAssignment) {
			switch (item.phaseId) {
				case DB_PHASES.P1:
					return { key: 'assignment.title.additional_draft', params: { careType } };
				case DB_PHASES.P3:
					return { key: 'assignment.title.additional_p3', params: { careType } };
				case DB_PHASES.P4:
					return { key: 'assignment.title.additional_p4', params: { careType } };
			}
		}

		if (this.isDraft(item)) {
			switch (item.draftPhaseId) {
				case DB_PHASES.P2:
					return { key: 'assignment.title.main_draft_p2' };
				case DB_PHASES.P3:
					return { key: 'assignment.title.main_draft_p3', params: { careType } };
			}
		}

		switch (item.phaseId) {
			case DB_PHASES.P2:
				return { key: 'assignment.title.main_p2' };

			case DB_PHASES.P3:
				return { key: 'assignment.title.main_p3', params: { careType } };

			case DB_PHASES.P4:
				return careType !== '-'
					? { key: 'assignment.title.main_p4', params: { careType } }
					: { key: 'assignment.title.main_p4_no_care_type' };

			default:
				return { key: `base.phase.${this.mapPhaseIdToPhase(item.phaseId)}` };
		}
	}

	public getDraftTitle(item: Assignment) {
		const draftPhaseId = item.draftPhaseId;
		if (!draftPhaseId) return;

		return this.mapPhaseIdToPhase(draftPhaseId);
	}

	public getAssignmentTitle(item: Assignment) {
		const phaseId = item.phaseId;
		if (!phaseId) return;

		return this.mapPhaseIdToPhase(phaseId);
	}

	private mapPhaseIdToPhase(phaseId: number) {
		switch (phaseId) {
			case DB_PHASES.P0:
				return 'p0';
			case DB_PHASES.P1:
				return 'p1';
			case DB_PHASES.P2:
				return 'p2';
			case DB_PHASES.P3:
				return 'p3';
			case DB_PHASES.P4:
				return 'p4';
			case DB_PHASES.FP3:
				return 'fp3';
			case DB_PHASES.FP4:
				return 'fp4';
			case DB_PHASES.FP5:
				return 'fp5';
			default:
				return 'unknown';
		}
	}

	public readonly deleteDraftRequest = output<Assignment>();

	public deleteDraft(event: Event, assignment: Assignment) {
		event.preventDefault();
		event.stopPropagation();
		this.deleteDraftRequest.emit(assignment);
	}

	public isExportLoadingFor(assignmentId: number) {
		return this.loading.isCurrentlyLoading(`contacts-pdf-${assignmentId}.export`, 'requestKey');
	}

	public async export(assignmentId: number, fileName: string = 'kontaktliste.pdf') {
		const uuid = this.loading.setLoadingState(window.crypto.randomUUID(), `contacts-pdf-${assignmentId}.export`);
		try {
			const blob = await this.uploadApi.getPdfOfContacts(assignmentId);
			const downloadURL = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = downloadURL;
			link.download = fileName;
			link.click();
			setTimeout(() => link.remove(), 200);
		} finally {
			this.loading.clearLoadingState(uuid);
		}
	}

	public pageIndexFor(id: number, preferenceKey: string) {
		return this.stateFor(this.pageStateKey(id, preferenceKey), preferenceKey).pageIndex;
	}
	public pageSizeFor(id: number, preferenceKey: string) {
		return this.stateFor(this.pageStateKey(id, preferenceKey), preferenceKey).pageSize;
	}

	private pageStateKey(id: number, preferenceKey: string): string {
		return `${this.clientId() ?? 0}:${id}:${preferenceKey}`;
	}

	private stateFor(key: string, preferenceKey: string) {
		const current = this.pageStateById()[key];
		if (current) return current;

		const savedPageSize = this.getSavedPageSize(preferenceKey);
		return { pageIndex: 0, pageSize: savedPageSize ?? this.environment.defaultPageSize };
	}

	public onContactsPage(id: number, e: PageEvent, preferenceKey: string) {
		const key = this.pageStateKey(id, preferenceKey);
		this.pageStateById.update((s) => ({
			...s,
			[key]: { pageIndex: e.pageIndex, pageSize: e.pageSize },
		}));
		this.savePageSize(preferenceKey, e.pageSize);
	}

	public visibleContacts(id: number, contacts: Contact[], preferenceKey: string) {
		const { pageIndex, pageSize } = this.stateFor(this.pageStateKey(id, preferenceKey), preferenceKey);

		if (this.canReadContactsRestricted()) {
			// Limit to the latest 3 contacts
			return contacts.slice(0, 3);
		}

		const start = pageIndex * pageSize;
		return contacts.slice(start, start + pageSize);
	}

	public isContactsLoading(item: Assignment): boolean {
		return this.contactLoadingByAssignmentId()[item.id] === true;
	}

	public hasContactsError(item: Assignment): boolean {
		return this.contactErrorByAssignmentId()[item.id] === true;
	}

	public getContactCount(item: Assignment): number {
		return item.contactsCount ?? item.contacts.length;
	}

	private getCurrentUserId(): string | undefined {
		return this.storage?.resolveCurrentUserId(this.auth);
	}

	private getSavedPageSize(preferenceKey: string): number | undefined {
		const userId = this.getCurrentUserId();
		if (!userId || !this.storage) return undefined;

		const saved = this.storage.getPageSize(userId, preferenceKey);
		if (!saved || !this.environment.pageSizeOptions.includes(saved)) return undefined;
		return saved;
	}

	private savePageSize(preferenceKey: string, pageSize: number): void {
		const userId = this.getCurrentUserId();
		if (!userId || !this.storage) return;
		this.storage.setPageSize(userId, preferenceKey, pageSize);
	}
}
